import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { translateTextStream } from '@services/aiTranslateService';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { toast } from 'react-toastify';
import AddressAutocomplete from '../common/AddressAutocomplete';
import AreaSuggestion from '../common/AreaSuggestion';
import { matchPlaceToAreaWithAutoSelect, MatchWithAutoSelectResult } from '../../utils/areaMatchingUtils';

interface DayOffOption {
  value: number;
  label: string;
}

const dayOffOptions: DayOffOption[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' }
];

interface SubServiceTypeOption {
  _id: string;
  name: string;
  zh_name: string;
}

interface AreaLocation {
  _id: string;
  name?: string;
  zh_name?: string;
  location_code?: string;
  chinese?: string;
  english?: string;
}

interface BusinessArea {
  _id?: string;
  name?: string;
  zh_name?: string;
  location_code?: string;
}

interface PlaceSelectedResult {
  address: string;
  coordinates: [number, number] | null;
  place_id: string;
}

interface RegionData {
  _id: string;
  region_chinese?: string;
  region_english?: string;
  locations?: AreaLocation[];
}

type StateSetter = React.Dispatch<React.SetStateAction<string>> | ((value: string | ((prev: string) => string)) => void);
type LanguageCode = 'en' | 'zh' | 'zh-HK' | 'zh-TW';

interface StylistProfileStepProps {
  displayName: string;
  setDisplayName: (value: string) => void;
  yearOfExperience: number | string;
  setYearOfExperience: (value: string) => void;
  canCommunicateInEnglish: boolean;
  setCanCommunicateInEnglish: (value: boolean) => void;
  description: string;
  setDescription: StateSetter;
  zh_description: string;
  setZhDescription: StateSetter;
  previewUrl?: string;
  handleAvatarSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dayOff: number[];
  setDayOff: (value: number[]) => void;
  startHour: string;
  setStartHour: (value: string) => void;
  endHour: string;
  setEndHour: (value: string) => void;
  subServiceTypes: SubServiceTypeOption[];
  setSubServiceTypes: (value: SubServiceTypeOption[]) => void;
  subServiceTypesOptions: SubServiceTypeOption[];
  isLoading?: boolean;
  useBusinessAddress: boolean;
  setUseBusinessAddress: (value: boolean) => void;
  businessAddress: string;
  onInputChange: (value: string) => void;
  businessArea?: BusinessArea | null;
  setBusinessArea: (value: AreaLocation | null) => void;
  areaLocations: AreaLocation[];
  onPlaceSelected: (placeData: PlaceSelectedResult) => void;
  placeId?: string;
  GoogleMapsLinks?: React.ComponentType<{ placeId: string; address: string }>;
  areaData?: RegionData[];
}

const StylistProfileStep: React.FC<StylistProfileStepProps> = ({
  displayName,
  setDisplayName,
  yearOfExperience,
  setYearOfExperience,
  canCommunicateInEnglish,
  setCanCommunicateInEnglish,
  description,
  setDescription,
  zh_description,
  setZhDescription,
  previewUrl,
  handleAvatarSelect,
  dayOff,
  setDayOff,
  startHour,
  setStartHour,
  endHour,
  setEndHour,
  subServiceTypes,
  setSubServiceTypes,
  subServiceTypesOptions,
  isLoading,
  useBusinessAddress,
  setUseBusinessAddress,
  businessAddress,
  onInputChange,
  businessArea,
  setBusinessArea,
  areaLocations,
  onPlaceSelected,
  placeId,
  GoogleMapsLinks,
  areaData = []
}) => {
  const { t } = useTranslation();
  const [isTranslatingEnToZhDesc, setIsTranslatingEnToZhDesc] = useState<boolean>(false);
  const [isTranslatingZhToEnDesc, setIsTranslatingZhToEnDesc] = useState<boolean>(false);
  const [areaSuggestion, setAreaSuggestion] = useState<MatchWithAutoSelectResult | null>(null);

  const handleStreamTranslate = async (
    sourceText: string,
    sourceLang: LanguageCode,
    targetLang: LanguageCode,
    setTargetState: StateSetter,
    setLoadingState: (loading: boolean) => void
  ): Promise<void> => {
    if (!sourceText) {
      toast.info('Source field for translation is empty.');
      return;
    }
    setLoadingState(true);

    await translateTextStream(
      sourceText,
      targetLang,
      sourceLang,
      setTargetState,
      () => { // onStreamComplete
        setLoadingState(false);
        toast.success('Translation streamed successfully!');
      },
      (error: Error) => { // onStreamError
        setLoadingState(false);
        toast.error(error.message || 'Translation streaming failed.');
      }
    );
  };

  const handleAreaSuggestionAccept = (payload: { regionId: string; locationId: string; match: { chinese?: string; english?: string } }): void => {
    console.log('[StylistProfileStep] Accepting area suggestion:', payload);
    // Set the business area based on the suggestion - find full object from areaLocations
    const fullArea = areaLocations.find(loc => loc._id === payload.locationId);
    setBusinessArea(fullArea || null);

    // Clear the suggestion
    setAreaSuggestion(null);

    // Show success message
    toast.success(t('areaSuggestion.accepted', {
      area: payload.match?.chinese || payload.match?.english
    }));
  };

  const handleAreaSuggestionDismiss = (): void => {
    setAreaSuggestion(null);
  };

  const enhancedOnPlaceSelected = (placeData: PlaceSelectedResult): void => {
    // Call the original onPlaceSelected handler
    onPlaceSelected(placeData);

    // Perform area matching
    if (areaData.length > 0) {
      const matchResult = matchPlaceToAreaWithAutoSelect(
        { address: placeData.address },
        areaData as { _id: string; region_code: string; region_chinese: string; region_english: string; locations?: { _id: string; english: string; chinese: string; location_code: string; [key: string]: unknown }[] }[]
      );
      console.log('[StylistProfileStep] Area match result:', matchResult);

      if (matchResult.shouldAutoSelect && matchResult.match) {
        // Auto-select the best match - find full object from areaLocations
        console.log('[StylistProfileStep] Auto-selecting area:', matchResult.match);
        const fullArea = areaLocations.find(loc => loc._id === matchResult.match!.location_id);
        setBusinessArea(fullArea || null);
        toast.success(t('areaSuggestion.autoSelected', {
          area: matchResult.match.chinese || matchResult.match.english,
          confidence: matchResult.confidence
        }));
      } else if (matchResult.shouldShowSuggestion) {
        // Show manual suggestion for lower confidence matches
        setAreaSuggestion(matchResult);
      }
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('quickListingWizard.setupStylistProfile')}
      </Typography>

      {(isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Profile Photo Upload */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('quickListingWizard.profilePhoto')}*
        </Typography>
        <Button
          variant="contained"
          component="label"
          fullWidth
          sx={{ mb: 2 }}
          disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
        >
          {t('stylistProfileSettings.selectAvatar')}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleAvatarSelect}
          />
        </Button>
        {previewUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img
              src={previewUrl}
              alt="Profile Preview"
              style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover' }}
            />
          </Box>
        )}
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.displayName')}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        required
        disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.yearOfExperience')}
        type="number"
        value={yearOfExperience}
        onChange={(e) => setYearOfExperience(e.target.value)}
        inputProps={{ min: 1 }}
        required
        error={Number(yearOfExperience) <= 0}
        helperText={Number(yearOfExperience) <= 0 ? t('quickListingWizard.invalidYearOfExperience') : ''}
        disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
      />

      {/* Use Business Address Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={useBusinessAddress}
            onChange={(e) => setUseBusinessAddress(e.target.checked)}
            disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
          />
        }
        label={t('stylistProfileSettings.useBusinessAddress')}
      />

      {/* Conditional Address and Area Fields */}
      {!useBusinessAddress && (
        <>
          <Box my={2}>
            <AddressAutocomplete
              label={t('stylistProfileSettings.businessAddress')}
              value={businessAddress}
              onInputChange={onInputChange}
              onPlaceSelected={enhancedOnPlaceSelected}
              disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
            />
            {/* Google Maps Links */}
            {placeId && businessAddress && GoogleMapsLinks && (
              <GoogleMapsLinks placeId={placeId} address={businessAddress} />
            )}

            {/* Area Suggestion */}
            {areaSuggestion && (
              <AreaSuggestion
                matchResult={areaSuggestion as React.ComponentProps<typeof AreaSuggestion>['matchResult']}
                onAcceptSuggestion={handleAreaSuggestionAccept as React.ComponentProps<typeof AreaSuggestion>['onAcceptSuggestion']}
                onDismiss={handleAreaSuggestionDismiss}
                currentSelection={businessArea?._id}
                showDebugInfo={import.meta.env.DEV}
              />
            )}
          </Box>
          <FormControl fullWidth margin="normal" disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}>
            <InputLabel>{t('stylistProfileSettings.businessArea')}</InputLabel>
            <Select
              value={businessArea?._id || ''}
              onChange={(e) => {
                const selectedArea = areaLocations.find(loc => loc._id === e.target.value);
                setBusinessArea(selectedArea || null);
              }}
              label={t('stylistProfileSettings.businessArea')}
              required
            >
              {areaLocations.map(location => (
                <MenuItem key={location._id} value={location._id}>
                  {location.zh_name} ({location.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {/* Sub-Service Types Field */}
      <FormControl fullWidth margin="normal">
        <Autocomplete
          multiple
          options={subServiceTypesOptions}
          value={subServiceTypes}
          onChange={(_, newValue) => setSubServiceTypes(newValue)}
          getOptionLabel={(option) => `${option.name} (${option.zh_name})`}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${t('common.subServiceTypes')}*`}
              placeholder={t('common.selectSubServiceTypes')}
              disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
            />
          )}
          isOptionEqualToValue={(option, value) => option._id === value._id}
        />
      </FormControl>

      {/* Day Off Field */}
      <FormControl fullWidth margin="normal" disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}>
        <InputLabel>{t('stylistProfileSettings.dayOff')}</InputLabel>
        <Select
          multiple
          value={dayOff}
          onChange={(e) => setDayOff(e.target.value as number[])}
          label={t('stylistProfileSettings.dayOff')}
        >
          {dayOffOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Working Hours Field */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
        <Typography>{t('stylistProfileSettings.workingHours')}*</Typography>
        <TimePicker
          label={t('stylistProfileSettings.startTime')}
          value={startHour ? dayjs().hour(parseInt(startHour.split(':')[0])).minute(parseInt(startHour.split(':')[1])) : null}
          onChange={(newValue: Dayjs | null) => {
            if (!newValue || !dayjs.isDayjs(newValue)) {
              setStartHour('');
              return;
            }
            const hours = String(newValue.hour()).padStart(2, '0');
            const minutes = String(newValue.minute()).padStart(2, '0');
            setStartHour(`${hours}:${minutes}`);
          }}
          slotProps={{ textField: { size: "small", disabled: isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc } }}
        />
        <Typography>{t('stylistProfileSettings.to')}</Typography>
        <TimePicker
          label={t('stylistProfileSettings.endTime')}
          value={endHour ? dayjs().hour(parseInt(endHour.split(':')[0])).minute(parseInt(endHour.split(':')[1])) : null}
          onChange={(newValue: Dayjs | null) => {
            if (!newValue || !dayjs.isDayjs(newValue)) {
              setEndHour('');
              return;
            }
            const hours = String(newValue.hour()).padStart(2, '0');
            const minutes = String(newValue.minute()).padStart(2, '0');
            setEndHour(`${hours}:${minutes}`);
          }}
          slotProps={{ textField: { size: "small", disabled: isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc } }}
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={canCommunicateInEnglish}
            onChange={(e) => setCanCommunicateInEnglish(e.target.checked)}
            disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
          />
        }
        label={t('quickListingWizard.canCommunicateInEnglish')}
      />

      {/* English Description Field with its Translate Button */}
      <Box sx={{ mt: 2, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={() => handleStreamTranslate(description, 'en', 'zh-HK', setZhDescription, setIsTranslatingEnToZhDesc)}
          disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc || isLoading || !description}
          variant="outlined"
          size="small"
          startIcon={isTranslatingEnToZhDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
        </Button>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.description')}
        multiline
        rows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
      />

      {/* Chinese Description Field with its Translate Button */}
      <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={() => handleStreamTranslate(zh_description, 'zh-HK', 'en', setDescription, setIsTranslatingZhToEnDesc)}
          disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc || isLoading || !zh_description}
          variant="outlined"
          size="small"
          startIcon={isTranslatingZhToEnDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
        </Button>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.zhDescription')}
        multiline
        rows={3}
        value={zh_description}
        onChange={(e) => setZhDescription(e.target.value)}
        required
        disabled={isLoading || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
      />
    </Box>
  );
};

export default StylistProfileStep;
